import React, { useState, useEffect } from 'react';
import { downloadCloneAsset } from '../utils/api';

const CLONE_STATUS = {
  pending: { bg: 'rgba(245,158,11,0.10)', color: 'var(--accent-a)', label: 'Pendente' },
  running: { bg: 'rgba(67,97,238,0.10)', color: 'var(--accent)', label: 'Rodando' },
  done:    { bg: 'rgba(6,185,111,0.10)', color: 'var(--accent-g)', label: 'Pronto' },
  error:   { bg: 'rgba(239,68,68,0.10)', color: 'var(--accent-r)', label: 'Erro' },
};

export default function Monitoramento() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('current');
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = () => {
    fetch('/api/monitoring/offers')
      .then(r => r.json())
      .then(d => {
        setOffers(d.data || []);
        setLoading(false);
        // Pegar timestamp do ultimo check
        const latest = (d.data || []).reduce((max, o) => {
          const t = o.adlib_last_checked ? new Date(o.adlib_last_checked).getTime() : 0;
          return t > max ? t : max;
        }, 0);
        if (latest) setLastUpdate(new Date(latest));
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const refresh = () => {
    setLoading(true);
    fetch('/api/monitoring/run', { method: 'POST' })
      .then(r => r.json())
      .then(() => load())
      .catch(() => setLoading(false));
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min atras`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atras`;
    return `${Math.floor(diff / 86400)}d atras`;
  };

  // Filtrar
  let filtered = offers;
  if (filter !== 'all') {
    if (filter === 'active') filtered = filtered.filter(o => (o.adlib_current || 0) > 0);
    else if (filter === 'dead') filtered = filtered.filter(o => (o.adlib_current || 0) === 0 || o.adlib_status === 'morto');
    else filtered = filtered.filter(o => o.adlib_status === filter);
  }
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(o =>
      (o.domain || '').toLowerCase().includes(q) ||
      (o.display_domain || '').toLowerCase().includes(q) ||
      (o.nicho || '').toLowerCase().includes(q) ||
      (o.pages || []).some(p => (p.name || '').toLowerCase().includes(q))
    );
  }

  // Ordenar
  filtered = [...filtered].sort((a, b) => {
    if (sort === 'current') return (b.adlib_current || 0) - (a.adlib_current || 0);
    if (sort === 'ads') return (b.ad_count || 0) - (a.ad_count || 0);
    if (sort === 'pages') return (b.page_count || 0) - (a.page_count || 0);
    if (sort === 'variation') {
      const va = a.adlib_7d_ago > 0 ? (a.adlib_current - a.adlib_7d_ago) / a.adlib_7d_ago : 0;
      const vb = b.adlib_7d_ago > 0 ? (b.adlib_current - b.adlib_7d_ago) / b.adlib_7d_ago : 0;
      return vb - va;
    }
    return (b.adlib_current || 0) - (a.adlib_current || 0);
  });

  // Status counts
  const counts = {
    all: offers.length,
    active: offers.filter(o => (o.adlib_current || 0) > 0).length,
    escalando: offers.filter(o => o.adlib_status === 'escalando').length,
    estavel: offers.filter(o => o.adlib_status === 'estavel').length,
    caindo: offers.filter(o => o.adlib_status === 'caindo').length,
    dead: offers.filter(o => (o.adlib_current || 0) === 0 || o.adlib_status === 'morto').length,
  };

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div>
          <div style={s.title}>Monitoramento de Ofertas</div>
          <div style={s.subtitle}>
            {lastUpdate ? `Ultima atualizacao: ${timeAgo(lastUpdate)}` : 'Acompanhe ads ativos no Ad Library'}
          </div>
        </div>
        <button style={s.refreshBtn} onClick={refresh} disabled={loading}>
          {loading ? 'Atualizando...' : 'Atualizar Ad Library'}
        </button>
      </div>

      {/* Filters */}
      <div style={s.filterBar}>
        <div style={s.filterGroup}>
          {[
            { key: 'all', label: 'Todas' },
            { key: 'active', label: 'Ativas' },
            { key: 'escalando', label: 'Escalando' },
            { key: 'estavel', label: 'Estavel' },
            { key: 'caindo', label: 'Caindo' },
            { key: 'dead', label: 'Mortas' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{ ...s.chip, ...(filter === f.key ? s.chipOn : {}) }}>
              {f.label} ({counts[f.key] || 0})
            </button>
          ))}
        </div>
        <div style={s.filterRight}>
          <select style={s.select} value={sort} onChange={e => setSort(e.target.value)}>
            <option value="current">Ads ativos</option>
            <option value="variation">Variacao 7D</option>
            <option value="ads">Ads minerados</option>
            <option value="pages">Paginas</option>
          </select>
          <input style={s.search} placeholder="Buscar dominio..." value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Oferta</th>
              <th style={s.th}>Nicho</th>
              <th style={s.th}>Paginas</th>
              <th style={s.th}>Minerados</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Ads Ativos</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Var. 7D</th>
              <th style={s.th}>Historico</th>
              <th style={s.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => <OfferRow key={o.id} offer={o} />)}
            {loading && (
              <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
                Carregando...
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
                {search ? 'Nenhuma oferta encontrada' : 'Nenhuma oferta monitorada'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={s.footer}>
        {filtered.length} de {offers.length} ofertas
      </div>
    </div>
  );
}

function OfferRow({ offer }) {
  const [expanded, setExpanded] = useState(false);
  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(false);
  const [clones, setClones] = useState([]);
  const [loadingClones, setLoadingClones] = useState(false);

  const refetchClones = () => {
    fetch(`/api/clone-site?offer_id=${offer.id}`)
      .then(r => r.json())
      .then(d => setClones(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  // Auto-refresh enquanto houver clone em pending/running
  useEffect(() => {
    if (!expanded) return;
    const hasActive = clones.some(c => c.status === 'pending' || c.status === 'running');
    if (!hasActive) return;
    const iv = setInterval(refetchClones, 3000);
    return () => clearInterval(iv);
  }, [expanded, clones]); // eslint-disable-line
  const o = offer;
  const current = o.adlib_current || 0;
  const prev7d = o.adlib_7d_ago || 0;
  const yesterday = o.adlib_yesterday || 0;

  const variation = prev7d > 0 ? ((current - prev7d) / prev7d * 100) : null;
  const variationText = variation !== null ? `${variation > 0 ? '+' : ''}${variation.toFixed(0)}%` : '—';
  const variationColor = variation > 0 ? 'var(--accent-g)' : variation < 0 ? 'var(--accent-r)' : 'var(--text3)';

  const statusConfig = {
    escalando: { bg: 'rgba(6,185,111,0.08)', color: 'var(--accent-g)', label: 'Escalando' },
    estavel: { bg: 'rgba(67,97,238,0.08)', color: 'var(--accent)', label: 'Estavel' },
    caindo: { bg: 'rgba(239,68,68,0.08)', color: 'var(--accent-r)', label: 'Caindo' },
    morto: { bg: 'rgba(136,136,160,0.06)', color: 'var(--text3)', label: 'Morto' },
    unknown: { bg: 'rgba(245,158,11,0.06)', color: 'var(--accent-a)', label: 'Novo' },
  };
  const st = statusConfig[o.adlib_status] || statusConfig.unknown;

  const fmt = (n) => {
    if (!n) return '0';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  };

  const toggle = () => {
    if (!expanded && ads.length === 0) {
      setLoadingAds(true);
      fetch(`/api/ads?offer_id=${o.id}&period=all&limit=20&sort=mined_at&order=desc`)
        .then(r => r.json())
        .then(d => { setAds(d.data || []); setLoadingAds(false); })
        .catch(() => setLoadingAds(false));
      setLoadingClones(true);
      fetch(`/api/clone-site?offer_id=${o.id}`)
        .then(r => r.json())
        .then(d => { setClones(Array.isArray(d) ? d : []); setLoadingClones(false); })
        .catch(() => setLoadingClones(false));
    }
    setExpanded(!expanded);
  };

  const adLibUrl = `https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=ALL&is_targeted_country=false&media_type=all&q=%22${encodeURIComponent(o.display_domain || o.domain)}%22&search_type=keyword_exact_phrase`;

  return (
    <>
      <tr style={{ opacity: current === 0 ? 0.5 : 1, cursor: 'pointer' }} onClick={toggle}>
        <td style={s.td}>
          {o.product_name && <div style={s.productName}>{o.product_name}</div>}
          <div style={o.product_name ? s.offerSub : s.offerName}>{o.display_domain || o.domain}</div>
          {o.display_domain && o.display_domain !== o.domain && !o.product_name && (
            <div style={s.offerSub}>{o.domain}</div>
          )}
        </td>
        <td style={s.td}>
          {o.nicho && <span style={s.nicheBadge}>{o.nicho}</span>}
        </td>
        <td style={s.td}>
          <span style={s.pageCount}>{o.page_count}</span>
        </td>
        <td style={s.td}>{o.ad_count}</td>
        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
          {fmt(current)}
        </td>
        <td style={{ ...s.td, textAlign: 'right', color: variationColor, fontWeight: 600 }}>
          {variationText}
        </td>
        <td style={s.td}>
          <Sparkline data={o.history} />
        </td>
        <td style={s.td}>
          <span style={{ ...s.statusBadge, background: st.bg, color: st.color }}>
            {st.label}
          </span>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} style={{ padding: 0, background: 'var(--surface2)' }}>
            <div style={s.detail}>
              {/* Metricas */}
              <div style={s.detailMetrics}>
                <div style={s.detailCard}>
                  <div style={s.detailLabel}>Ads Ativos</div>
                  <div style={s.detailValue}>{fmt(current)}</div>
                </div>
                <div style={s.detailCard}>
                  <div style={s.detailLabel}>Ontem</div>
                  <div style={s.detailValue}>{fmt(yesterday)}</div>
                </div>
                <div style={s.detailCard}>
                  <div style={s.detailLabel}>7 dias atras</div>
                  <div style={s.detailValue}>{fmt(prev7d)}</div>
                </div>
                <div style={s.detailCard}>
                  <div style={s.detailLabel}>Variacao 7D</div>
                  <div style={{ ...s.detailValue, color: variationColor }}>{variationText}</div>
                </div>
                <div style={s.detailCard}>
                  <div style={s.detailLabel}>1a Captura</div>
                  <div style={{ ...s.detailValue, fontSize: 12 }}>{o.first_seen_at ? new Date(o.first_seen_at).toLocaleDateString('pt-BR') : '—'}</div>
                </div>
              </div>

              {/* Historico chart — linha */}
              {o.history && o.history.length >= 2 && (
                <div style={s.detailSection}>
                  <div style={s.detailSectionTitle}>Historico Ad Library</div>
                  <HistoryLineChart data={o.history} />
                </div>
              )}

              {/* Paginas */}
              <div style={s.detailSection}>
                <div style={s.detailSectionTitle}>Paginas que promovem ({o.page_count})</div>
                <div style={s.detailPages}>
                  {(o.pages || []).map((p, i) => (
                    <div key={i} style={s.detailPage}>
                      <span style={{ fontWeight: 500 }}>{p.name || '?'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ofertas (agrupadas por URL base) + VSL */}
              {!loadingAds && ads.length > 0 && (() => {
                const urlBase = (url) => { try { const u = new URL(url); return u.origin + u.pathname; } catch { return url; } };
                const urlClean = (url) => { try { const u = new URL(url); return u.hostname + u.pathname; } catch { return url; } };
                const groups = {};
                ads.forEach(a => {
                  if (!a.real_url) return;
                  const base = urlBase(a.real_url);
                  if (!groups[base]) groups[base] = { url: base, fullUrl: a.real_url, cleanUrl: urlClean(a.real_url), ads: [], pages: new Set() };
                  groups[base].ads.push(a);
                  if (a.fb_page_name) groups[base].pages.add(a.fb_page_name);
                });
                const offerList = Object.values(groups);
                return (
                  <div style={s.detailSection}>
                    <div style={s.detailSectionTitle}>Ofertas ({offerList.length})</div>
                    <div style={s.offerList}>
                      {offerList.map((of, i) => (
                        <OfferLine key={i} offer={of} offerId={o.id} vslPaths={o.vsl_paths || {}} onCloneCreated={refetchClones} />
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Ads minerados */}
              <div style={s.detailSection}>
                <div style={s.detailSectionTitle}>Ads minerados ({o.ad_count})</div>
                {loadingAds ? (
                  <div style={{ color: 'var(--text3)', fontSize: 12 }}>Carregando...</div>
                ) : ads.length > 0 ? (
                  <div style={s.adGrid}>
                    {ads.map((a, i) => (
                      <div key={i} style={s.adCard}>
                        <div style={s.adCardTop}>
                          {a.ad_link ? (
                            <a href={a.ad_link} target="_blank" rel="noopener" style={s.adCardName}>{a.fb_page_name}</a>
                          ) : (
                            <span style={{ ...s.adCardName, color: 'var(--text)' }}>{a.fb_page_name}</span>
                          )}
                        </div>
                        {a.copy_text && <div style={s.adCardCopy}>{a.copy_text.slice(0, 80)}</div>}
                        <div style={s.adCardEngRow}>
                          <span>Likes <b>{a.likes || 0}</b></span>
                          <span>Comm <b>{a.comments || 0}</b></span>
                          <span>Shares <b>{a.shares || 0}</b></span>
                        </div>
                        <div style={s.adCardFooter}>
                          <span>{a.mined_at ? new Date(a.mined_at).toLocaleDateString('pt-BR') : ''}</span>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {a.ad_link && <a href={a.ad_link} target="_blank" rel="noopener" style={s.adCardLink}>Ver no FB</a>}
                            {a.real_url && <a href={a.real_url} target="_blank" rel="noopener" style={{ ...s.adCardLink, color: 'var(--accent-g)' }}>Landing Page</a>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: 'var(--text3)', fontSize: 12 }}>Nenhum ad minerado</div>
                )}
              </div>

              {/* Clones de LP */}
              <div style={s.detailSection}>
                <div style={s.detailSectionTitle}>Clones de LP ({clones.length})</div>
                {loadingClones ? (
                  <div style={{ color: 'var(--text3)', fontSize: 12 }}>Carregando...</div>
                ) : clones.length > 0 ? (
                  <div style={s.offerList}>
                    {clones.map(c => <CloneLine key={c.id} clone={c} />)}
                  </div>
                ) : (
                  <div style={{ color: 'var(--text3)', fontSize: 12 }}>Nenhum clone ainda. Clique "Clonar LP" numa oferta acima.</div>
                )}
              </div>

              {/* Link Ad Library */}
              <div style={s.detailActions}>
                <a href={adLibUrl} target="_blank" rel="noopener" style={s.adLibLink}>
                  Ver no Ad Library
                </a>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function OfferLine({ offer, offerId, vslPaths, onCloneCreated }) {
  const [downloading, setDownloading] = useState(false);
  const [showVsl, setShowVsl] = useState(false);
  const [cloning, setCloning] = useState(false);

  // Slug da URL (path sem params)
  const slug = (() => { try { return new URL(offer.url).pathname.replace(/\/$/, '') || '/'; } catch { return '/'; } })();
  const vsl = vslPaths[slug] || null;

  const downloadVsl = (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    fetch('/api/monitoring/download-vsl', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ offer_id: offerId, url: offer.fullUrl }),
    })
      .then(r => r.json())
      .then(() => { setDownloading(false); window.location.reload(); })
      .catch(() => setDownloading(false));
  };

  const cloneLp = (e) => {
    e.stopPropagation();
    if (cloning) return;
    setCloning(true);
    fetch('/api/clone-site', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: offer.fullUrl, offer_id: offerId }),
    })
      .then(r => r.json())
      .then(() => { setCloning(false); if (onCloneCreated) onCloneCreated(); })
      .catch(() => { setCloning(false); alert('Falha ao iniciar clone'); });
  };

  return (
    <>
      <div style={s.offerItem}>
        <a href={offer.fullUrl} target="_blank" rel="noopener" style={s.offerUrl}>{offer.cleanUrl}</a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={s.offerMeta}>{offer.ads.length} ads · {[...offer.pages].join(', ')}</span>
          {vsl ? (
            <button style={s.vslBtn} onClick={(e) => { e.stopPropagation(); setShowVsl(true); }}>▶ VSL</button>
          ) : (
            <button style={s.vslDownloadBtn} onClick={downloadVsl} disabled={downloading}>
              {downloading ? 'Baixando...' : 'Baixar VSL'}
            </button>
          )}
          <button style={s.cloneBtn} onClick={cloneLp} disabled={cloning} title="Clona a LP em background e gera ZIP com pontos a trocar">
            {cloning ? 'Clonando...' : 'Clonar LP'}
          </button>
        </div>
      </div>
      {showVsl && vsl && (
        <div style={s.vslModal} onClick={() => setShowVsl(false)}>
          <div style={s.vslModalContent} onClick={e => e.stopPropagation()}>
            <button style={s.vslModalClose} onClick={() => setShowVsl(false)}>X</button>
            <video src={vsl} controls autoPlay style={s.vslVideo} />
          </div>
        </div>
      )}
    </>
  );
}

function CloneLine({ clone }) {
  const status = CLONE_STATUS[clone.status] || CLONE_STATUS.pending;
  const stats = clone.stats || {};
  const cleanUrl = (() => {
    try { const u = new URL(clone.source_url); return u.hostname + u.pathname; }
    catch { return clone.source_url; }
  })();

  const handleDownload = (e) => {
    e.stopPropagation();
    downloadCloneAsset(clone.id, 'download').catch(err => alert('Erro ao baixar: ' + err.message));
  };
  const handleMd = (e) => {
    e.stopPropagation();
    downloadCloneAsset(clone.id, 'instructions').catch(err => alert('Erro ao baixar: ' + err.message));
  };

  return (
    <div style={s.offerItem}>
      <a href={clone.source_url} target="_blank" rel="noopener" style={s.offerUrl} title={clone.source_url}>{cleanUrl}</a>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...s.statusBadge, background: status.bg, color: status.color, fontSize: 10 }}>{status.label}</span>
        {clone.status === 'done' && (
          <span style={s.offerMeta}>{stats.total_findings || 0} pts · {stats.assets_downloaded || 0} assets</span>
        )}
        {clone.status === 'error' && clone.error_msg && (
          <span style={{ ...s.offerMeta, color: 'var(--accent-r)' }} title={clone.error_msg}>{clone.error_msg.slice(0, 40)}</span>
        )}
        {clone.has_zip && <button style={s.vslDownloadBtn} onClick={handleDownload}>ZIP</button>}
        {clone.has_zip && <button style={s.vslDownloadBtn} onClick={handleMd}>MD</button>}
      </div>
    </div>
  );
}

function fillDays(data, days) {
  if (!data || data.length === 0) return [];
  const map = {};
  data.forEach(d => { map[d.date] = d.count; });
  // Ancora no MAIS RECENTE entre [hoje] e [ultimo dia com dados], para que
  // ofertas com monitoring stale (sem coleta recente) ainda mostrem a curva
  // historica em vez de uma linha plana no zero.
  const today = new Date();
  const lastDataDay = data
    .map(d => new Date(d.date + "T00:00:00Z"))
    .reduce((max, dt) => (dt > max ? dt : max), new Date(0));
  const end = lastDataDay > today ? today : (lastDataDay > new Date(0) ? lastDataDay : today);
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    result.push({ date: key, count: map[key] || 0 });
  }
  return result;
}

function HistoryLineChart({ data: rawData }) {
  const ref = React.useRef(null);
  const [hover, setHover] = useState(null);
  const [width, setWidth] = useState(0);
  const data = fillDays(rawData, 30);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  if (data.length < 2) return null;

  const chartH = 100;
  const labelH = 18;
  const pad = { t: 6, b: 4, l: 6, r: 6 };
  const plotW = width - pad.l - pad.r;
  const plotH = chartH - pad.t - pad.b;
  const values = data.map(d => d.count);
  const max = Math.max(...values, 1);
  const fmt = (n) => n >= 1000 ? (n/1000).toFixed(1).replace(/\.0$/,'')+'K' : n;
  const fmtDate = (d) => { const p = d.split('-'); return `${p[2]}/${p[1]}`; };

  const points = width > 0 ? values.map((v, i) => ({
    x: pad.l + (i / (values.length - 1)) * plotW,
    y: pad.t + (1 - v / max) * plotH,
    value: v,
  })) : [];

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = points.length ? linePath + `L${points[points.length-1].x},${chartH - pad.b}L${points[0].x},${chartH - pad.b}Z` : '';

  return (
    <div ref={ref} style={{ position: 'relative', overflow: 'hidden' }}
      onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={chartH + labelH} style={{ display: 'block' }}>
          <defs>
            <linearGradient id="hlcGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.10" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Grid */}
          <line x1={pad.l} y1={pad.t + plotH * 0.5} x2={width - pad.r} y2={pad.t + plotH * 0.5}
            stroke="var(--border)" strokeWidth="0.5" opacity="0.1" />
          {/* Area + line */}
          <path d={areaPath} fill="url(#hlcGrad)" />
          <path d={linePath} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Hit columns */}
          {points.map((p, i) => (
            <rect key={i} x={p.x - plotW / points.length / 2} y={0}
              width={plotW / points.length} height={chartH}
              fill="transparent" style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHover(i)} />
          ))}
          {/* Hover dot */}
          {hover !== null && (
            <circle cx={points[hover].x} cy={points[hover].y} r={3}
              fill="var(--accent)" stroke="var(--surface)" strokeWidth="1.5" />
          )}
          {/* Date labels */}
          {points.map((p, i) => (i % 5 === 0 || i === points.length - 1) ? (
            <text key={i} x={p.x} y={chartH + 12}
              textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
              fontSize="10" fill="var(--text3)" opacity="0.5">
              {fmtDate(data[i].date)}
            </text>
          ) : null)}
          {/* Tooltip */}
          {hover !== null && (() => {
            const hp = points[hover];
            return (
              <g>
                <line x1={hp.x} y1={pad.t} x2={hp.x} y2={chartH - pad.b}
                  stroke="var(--border)" strokeWidth="0.5" />
                <rect x={Math.min(Math.max(hp.x - 36, 2), width - 74)}
                  y={hp.y > 36 ? hp.y - 32 : hp.y + 8}
                  width="72" height="26" rx="4" fill="rgba(0,0,0,0.85)" />
                <text x={Math.min(Math.max(hp.x, 38), width - 38)}
                  y={hp.y > 36 ? hp.y - 20 : hp.y + 22}
                  textAnchor="middle" fontSize="11" fontWeight="600" fill="#fff">
                  {fmt(hp.value)} ads
                </text>
                <text x={Math.min(Math.max(hp.x, 38), width - 38)}
                  y={hp.y > 36 ? hp.y - 10 : hp.y + 32}
                  textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.5)">
                  {fmtDate(data[hover].date)}
                </text>
              </g>
            );
          })()}
        </svg>
      )}
    </div>
  );
}

function Sparkline({ data: rawData }) {
  const data = rawData ? rawData.slice(-7) : [];
  if (data.length < 2) return <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>;
  const values = data.map(d => d.count);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max || 1;
  const w = 80;
  const h = 24;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');

  const trend = values[values.length - 1] >= values[0] ? 'var(--accent-g)' : 'var(--accent-r)';

  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={trend} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const s = {
  page: { padding: '24px', maxWidth: 1400, margin: '0 auto' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 13, fontWeight: 700 },
  subtitle: { fontSize: 12, color: 'var(--text3)', marginTop: 4 },
  refreshBtn: {
    padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  },

  // Filters
  filterBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' },
  filterGroup: { display: 'flex', gap: 4 },
  filterRight: { display: 'flex', gap: 8 },
  chip: { padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text3)', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.15s' },
  chipOn: { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  select: { padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', fontSize: 11, fontFamily: 'inherit', outline: 'none' },
  search: { padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 11, fontFamily: 'inherit', outline: 'none', width: 180 },

  // Table
  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 14px', fontSize: 10, color: 'var(--text3)',
    textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)',
    fontWeight: 600, whiteSpace: 'nowrap',
  },
  td: { padding: '10px 14px', borderBottom: '1px solid var(--surface3)', verticalAlign: 'middle' },

  // Offer
  productName: { fontWeight: 700, color: 'var(--text)', fontSize: 13 },
  offerName: { fontWeight: 600, color: 'var(--accent)', fontSize: 11 },
  offerSub: { fontSize: 10, color: 'var(--text3)', marginTop: 2 },

  // Badges
  nicheBadge: { padding: '2px 8px', background: 'rgba(167,139,250,0.12)', color: 'var(--accent-p)', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  statusBadge: { padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },

  // Pages
  pageCount: { fontWeight: 600 },
  pageNames: { display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2, fontSize: 10, color: 'var(--text3)' },

  // Detail panel
  detail: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 },
  detailMetrics: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  detailCard: { flex: '1 1 100px', padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' },
  detailLabel: { fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 },
  detailValue: { fontSize: 16, fontWeight: 700, marginTop: 2 },
  detailSection: {},
  detailSectionTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 },
  detailPages: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  detailPage: { padding: '4px 10px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 },
  adGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 },
  adCard: { padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 },
  adCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  adCardName: { fontSize: 13, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  adCardScore: { fontSize: 14, fontWeight: 700, flexShrink: 0 },
  adCardCopy: { fontSize: 11, color: 'var(--text3)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  adCardEngRow: { display: 'flex', gap: 12, fontSize: 11, color: 'var(--text2)' },
  adCardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, color: 'var(--text3)', borderTop: '1px solid var(--surface3)', paddingTop: 6 },
  adCardLink: { color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 },

  // Offer list
  offerList: { display: 'flex', flexDirection: 'column', gap: 0 },
  offerItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', gap: 12 },
  offerUrl: { fontSize: 12, color: 'var(--accent)', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 },
  offerItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', gap: 12, minWidth: 0 },
  offerMeta: { fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', flexShrink: 0 },
  vslBtn: { padding: '3px 10px', border: 'none', borderRadius: 4, background: 'var(--accent-g)', color: '#fff', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  vslDownloadBtn: { padding: '3px 10px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text3)', fontSize: 10, fontFamily: 'inherit', cursor: 'pointer' },
  cloneBtn: { padding: '3px 10px', border: 'none', borderRadius: 4, background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  vslModal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  vslModalContent: { position: 'relative', maxWidth: '90vw', maxHeight: '90vh' },
  vslModalClose: { position: 'absolute', top: -36, right: 0, background: 'none', border: 'none', color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer', padding: '4px 8px' },
  vslVideo: { maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 },
  detailActions: { display: 'flex', gap: 8 },
  adLibLink: { padding: '6px 14px', background: 'var(--accent)', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none' },

  // Footer
  footer: { marginTop: 8, fontSize: 11, color: 'var(--text3)', textAlign: 'right' },
};
